<template>
  <FormularioCadastro
    module-name="Usuários"
    :is-loading="isLoading"
    :is-editing="isEditing"
    :entity-info="entityInfo"
    :ocultar-salvar="!isUsuarioSaved"
    :desabilitar-salvar="isReadOnly"
    :desabilitar-salvar-alteracoes="isFramesSecundariosBloqueados || isReadOnly"
    @submit="submitFormComGruposERegras"
    @submit-continuar="salvarAlteracoes"
    @cancelar="handleCancel"
    @show-notification="(payload) => emit('show-notification', payload)"
  >
    <!-- Slot de Ações do Header -->
    <template #acoes-header v-if="isEditing">
      <button
        type="button"
        class="btn-icon-minimalista me-3"
        @click="mostrarAuditoria = true"
        title="Ver Histórico de Alterações"
      >
        <i class="bi bi-clock-history"></i>
      </button>
    </template>

    <!-- Frame 1: Dados Básicos -->
    <div class="card mb-4">
      <div
        class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.dadosBasicos = !accordionState.dadosBasicos"
      >
        <h5 class="mb-0"><i class="bi bi-info-circle me-2 text-primary"></i> Dados Básicos</h5>
        <i :class="['bi', accordionState.dadosBasicos ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.dadosBasicos">
        <div class="row g-3">

          <!-- GUARDIÃO: Substituição do Switch pelo CampoSelect Padrão -->
          <div v-if="authStore.isAdminInterno" class="col-md-4">
            <CampoSelect
              id="adminInternoSelect"
              label="Admin da Camuri Tech?"
              v-model="item.adminInterno"
              :options="simNaoOptions"
              option-value="id"
              option-label="label"
              :disabled="isReadOnly"
              required
            />
          </div>

          <div :class="authStore.isAdminInterno ? 'col-md-4' : 'col-md-6'">
            <CampoInput id="nomeUsuario" label="Nome" v-model="item.nome" required :readonly="isReadOnly" />
          </div>

          <div :class="authStore.isAdminInterno ? 'col-md-4' : 'col-md-6'">
            <CampoInput id="emailUsuario" label="Email" type="email" v-model="item.email" required :readonly="isReadOnly" />
          </div>

          <div class="col-md-6">
            <CampoInput id="nomeExibicaoUsuario" label="Nome de Guerra" v-model="item.nomeExibicao" :readonly="isReadOnly" />
            <small class="text-body-secondary">Máximo 3 palavras. Editável pelo usuário em "Meu Perfil".</small>
          </div>

          <!-- Bloco do Cliente -->
          <template v-if="!item.adminInterno">
            <div class="col-md-4" v-if="authStore.isAdminInterno">
              <CampoBusca
                id="empresaUsuario"
                label="Empresa"
                v-model="item.idEmpresa"
                :configuracao-busca="configBuscaEmpresa"
                @update:modelValue="onEmpresaChange"
                :disabled="isReadOnly"
              />
            </div>

            <!-- GUARDIÃO: Substituição do Switch pelo CampoSelect Padrão -->
            <div :class="authStore.isAdminInterno ? 'col-md-4' : 'col-md-6'">
              <CampoSelect
                id="adminClienteSelect"
                label="Admin do Cliente?"
                v-model="item.adminCliente"
                :options="simNaoOptions"
                option-value="id"
                option-label="label"
                :disabled="isReadOnly"
                required
              />
            </div>
          </template>

          <div :class="(!item.adminInterno && authStore.isAdminInterno) ? 'col-md-4' : 'col-md-6'">
            <CampoSelect
              id="statusUsuario"
              label="Status"
              v-model="item.statusUsuario"
              :options="opcoesStatus"
              option-value="id"
              option-label="descricao"
              :disabled="isReadOnly"
              required
            />
          </div>

          <div class="col-12 mt-3">
            <div class="d-flex align-items-center">
              <div class="alert alert-info py-2 px-3 mb-0 small me-3 flex-grow-1 border-0 shadow-sm">
                <template v-if="isEditing">
                  Para alterar a senha, utilize o botão Resetar.
                </template>
                <template v-else>
                  Um e-mail será enviado para definição de senha.
                </template>
              </div>
              <button v-if="isEditing" type="button" class="btn btn-outline-secondary btn-sm text-nowrap" @click="resetarSenha" :disabled="isLoadingReset || isReadOnly">
                <span v-if="isLoadingReset" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                Resetar Senha
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Botão Salvar e Continuar (apenas criação) -->
    <div class="mb-4" v-if="!isUsuarioSaved">
      <button
        type="button"
        class="btn btn-success px-4"
        @click="salvarUsuarioLocalmente"
        :disabled="isSavingUsuarioLocal || !item.nome || !item.email"
      >
        <span v-if="isSavingUsuarioLocal" class="spinner-border spinner-border-sm me-2"></span>
        <i v-else class="bi bi-save me-2"></i>
        Salvar Usuário e Liberar Grupos/Permissões
      </button>
    </div>

    <!-- Frame 2: Grupos de Acesso -->
    <div :class="{ 'pe-none opacity-50': isFramesSecundariosBloqueados }">
    <div class="card mb-4" v-if="!item.adminInterno || (authStore.isAdminInterno && item.idEmpresa)">
      <div
        class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.gruposAcesso = !accordionState.gruposAcesso"
      >
        <h5 class="mb-0"><i class="bi bi-people me-2 text-primary"></i> Grupos de Acesso</h5>
        <i :class="['bi', accordionState.gruposAcesso ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.gruposAcesso">
        <div v-if="isLoadingGrupos" class="text-center text-muted small py-3">
          <span class="spinner-border spinner-border-sm me-2"></span> A carregar grupos...
        </div>
        <div v-else-if="erroGrupos" class="alert alert-warning small border-0 shadow-sm">{{ erroGrupos }}</div>
        <div v-else>
          <!-- Botão Adicionar Grupo -->
          <div class="d-flex justify-content-between align-items-center mb-3">
            <span class="text-body-secondary small">
              <i class="bi bi-info-circle me-1"></i>
              Selecione os grupos de acesso que serão atribuídos ao usuário.
            </span>
            <button
              type="button"
              class="btn btn-outline-primary btn-sm"
              @click="abrirModalGrupos"
              :disabled="isReadOnly"
            >
              <i class="bi bi-plus-lg me-1"></i>
              Adicionar Grupo
            </button>
          </div>

          <!-- Grid de Grupos Selecionados -->
          <div v-if="gruposSelecionados.length > 0" class="table-responsive">
            <table class="table table-sm table-hover align-middle mb-0">
              <thead class="table-light">
                <tr>
                  <th style="width: 60px;">ID</th>
                  <th>Nome</th>
                  <th>Descrição</th>
                  <th style="width: 80px;" class="text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="grupo in gruposSelecionados" :key="grupo.idGrupoAcesso">
                  <td class="text-body-secondary">{{ grupo.idGrupoAcesso }}</td>
                  <td class="fw-medium">{{ grupo.nome }}</td>
                  <td class="text-body-secondary">{{ grupo.descricao || '-' }}</td>
                  <td class="text-center">
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-danger"
                      @click="removerGrupoSelecionado(grupo.idGrupoAcesso)"
                      :disabled="isReadOnly"
                      title="Remover grupo"
                    >
                      <i class="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="text-center text-muted small py-4 border border-secondary-subtle rounded">
            <i class="bi bi-people fs-2 d-block mb-2 text-secondary"></i>
            Nenhum grupo de acesso selecionado.
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="item.adminInterno" class="alert alert-secondary border-0 shadow-sm mt-3">A seleção de grupos não se aplica a Administradores Internos.</div>
    </div>

    <!-- Frame: Organizações (multi-organização) -->
    <div :class="{ 'pe-none opacity-50': isFramesSecundariosBloqueados }">
    <div class="card mb-4" v-if="!item.adminInterno">
      <div
        class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.organizacoes = !accordionState.organizacoes"
      >
        <h5 class="mb-0"><i class="bi bi-diagram-3 me-2 text-primary"></i> Organizações</h5>
        <i :class="['bi', accordionState.organizacoes ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.organizacoes">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <span class="text-body-secondary small">
            <i class="bi bi-info-circle me-1"></i>
            Selecione as organizações às quais o usuário terá acesso e defina a organização padrão (aberta no login).
          </span>
          <button
            type="button"
            class="btn btn-outline-primary btn-sm"
            @click="abrirModalOrganizacoes"
            :disabled="isReadOnly"
          >
            <i class="bi bi-plus-lg me-1"></i>
            Adicionar Organização
          </button>
        </div>

        <div v-if="organizacoesSelecionadas.length > 0" class="table-responsive">
          <table class="table table-sm table-hover align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th style="width: 60px;">ID</th>
                <th>Nome Fantasia</th>
                <th>Razão Social</th>
                <th style="width: 120px;" class="text-center">Padrão</th>
                <th style="width: 80px;" class="text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="org in organizacoesSelecionadas" :key="org.idOrganizacao">
                <td class="text-body-secondary">{{ org.idOrganizacao }}</td>
                <td class="fw-medium">{{ org.nomeFantasia }}</td>
                <td class="text-body-secondary">{{ org.razaoSocial }}</td>
                <td class="text-center">
                  <input
                    class="form-check-input cursor-pointer"
                    type="radio"
                    name="orgPadrao"
                    :value="org.idOrganizacao"
                    v-model="idOrganizacaoPadrao"
                    :disabled="isReadOnly"
                  >
                </td>
                <td class="text-center">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    @click="removerOrganizacaoSelecionada(org.idOrganizacao)"
                    :disabled="isReadOnly"
                    title="Remover organização"
                  >
                    <i class="bi bi-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="text-center text-muted small py-4 border border-secondary-subtle rounded">
          <i class="bi bi-diagram-3 fs-2 d-block mb-2 text-secondary"></i>
          Nenhuma organização selecionada.
        </div>
      </div>
    </div>
    <div v-else class="alert alert-secondary border-0 shadow-sm mt-3">A seleção de organizações não se aplica a Administradores Internos.</div>
    </div>

    <!-- Frame 3: Regras de Acesso Individuais (Permissões) -->
    <div :class="{ 'pe-none opacity-50': isFramesSecundariosBloqueados }">
    <div class="card mb-4">
      <div
        class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.regrasAcesso = !accordionState.regrasAcesso"
      >
        <h5 class="mb-0"><i class="bi bi-shield-check me-2 text-primary"></i> Regras de Acesso Individuais (Permissões)</h5>
        <div class="d-flex align-items-center">
          <i :class="['bi', accordionState.regrasAcesso ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
        </div>
      </div>
      <div class="card-body" v-show="accordionState.regrasAcesso">
        <div v-if="isLoadingPermissoes" class="text-center text-muted small py-4">
          <span class="spinner-border spinner-border-sm me-2"></span> A carregar permissões...
        </div>
        <div v-else-if="erroPermissoes" class="alert alert-warning small border-0 shadow-sm">{{ erroPermissoes }}</div>
        <PermissoesAcessoSelector
          v-else
          :permissoesConsolidadas="permissoesConsolidadas"
          v-model:modelValue="permissoesDiretasIds"
          :permissoesHerdadas="permissoesHerdadas"
          :disabled="isReadOnly"
          :exibirControleExpiracao="false"
          :exibirMapaPermissoes="true"
        />
      </div>
    </div>
    </div>

    <!-- Frame 4: Configurações de Sessão / Token JWT -->
    <div :class="{ 'pe-none opacity-50': isFramesSecundariosBloqueados }">
    <div class="card mb-4">
      <div
        class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.configToken = !accordionState.configToken"
      >
        <h5 class="mb-0"><i class="bi bi-shield-lock me-2 text-primary"></i> Configurações de Sessão / Token JWT</h5>
        <i :class="['bi', accordionState.configToken ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.configToken">
        <div v-if="isLoadingTokenConfig" class="text-center text-muted small py-3">
          <span class="spinner-border spinner-border-sm me-2"></span> Carregando...
        </div>

        <div v-else class="table-responsive">
          <table class="table table-sm table-hover align-middle">
            <thead class="table-light">
              <tr>
                <th style="width: 40px;">Ativar</th>
                <th>Origem</th>
                <th>Access Token (min)</th>
              </tr>
            </thead>
            <tbody>
              <tr :class="{ 'table-primary': item.tokenExpiracaoOrigemAtiva === 'FALLBACK' }">
                <td class="text-center">
                  <input class="form-check-input cursor-pointer" type="radio" name="origemAtiva"
                    value="FALLBACK" v-model="item.tokenExpiracaoOrigemAtiva">
                </td>
                <td><span class="fw-medium">Sistema (Fallback)</span></td>
                <td>{{ tokenExpiracaoConfig?.accessTokenFallback ?? 15 }}</td>
              </tr>
              <tr :class="{ 'table-primary': item.tokenExpiracaoOrigemAtiva === 'EMPRESA' }"
                  v-if="tokenExpiracaoConfig?.empresaAccessToken !== undefined">
                <td class="text-center">
                  <input class="form-check-input cursor-pointer" type="radio" name="origemAtiva"
                    value="EMPRESA" v-model="item.tokenExpiracaoOrigemAtiva">
                </td>
                <td>
                  <span class="fw-medium">Empresa</span>
                  <small v-if="!tokenExpiracaoConfig.empresaAccessToken" class="text-muted d-block">(usando fallback)</small>
                </td>
                <td>{{ tokenExpiracaoConfig.empresaAccessToken ?? tokenExpiracaoConfig.accessTokenFallback }}</td>
              </tr>
              <tr :class="{ 'table-primary': item.tokenExpiracaoOrigemAtiva === 'GRUPO' }"
                  v-if="tokenExpiracaoConfig?.gruposAccessToken !== undefined">
                <td class="text-center">
                  <input class="form-check-input cursor-pointer" type="radio" name="origemAtiva"
                    value="GRUPO" v-model="item.tokenExpiracaoOrigemAtiva">
                </td>
                <td>
                  <span class="fw-medium">Grupos de Acesso</span>
                  <small class="text-info d-block">(mais restritivo)</small>
                </td>
                <td>{{ tokenExpiracaoConfig.gruposAccessToken ?? tokenExpiracaoConfig.accessTokenFallback }}</td>
              </tr>
              <tr :class="{ 'table-primary': item.tokenExpiracaoOrigemAtiva === 'PERSONALIZADO' }">
                <td class="text-center">
                  <input class="form-check-input cursor-pointer" type="radio" name="origemAtiva"
                    value="PERSONALIZADO" v-model="item.tokenExpiracaoOrigemAtiva">
                </td>
                <td><span class="fw-medium">Personalizado</span></td>
                <td>
                  <input type="number" class="form-control form-control-sm" min="1"
                    v-model.number="item.tokenExpiracaoPersonalizadoMinutos"
                    :disabled="item.tokenExpiracaoOrigemAtiva !== 'PERSONALIZADO'"
                    placeholder="minutos">
                </td>
              </tr>
            </tbody>
          </table>
          <small class="text-body-secondary">
            <i class="bi bi-info-circle me-1"></i>
            A origem ativa define qual configuração o sistema usará ao gerar o token JWT.
            Se a origem selecionada não tiver valor, o sistema usa o fallback (15 min).
          </small>
        </div>
      </div>
    </div>
    </div>

    <!-- Frame 5: Perfil Público -->
    <div :class="{ 'pe-none opacity-50': isFramesSecundariosBloqueados }">
    <div class="card mb-4">
      <div
        class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.perfilPublico = !accordionState.perfilPublico"
      >
        <h5 class="mb-0"><i class="bi bi-person-badge me-2 text-primary"></i> Perfil Público</h5>
        <i :class="['bi', accordionState.perfilPublico ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.perfilPublico">
        <div class="row g-3">
          <!-- Nickname -->
          <div class="col-md-6">
            <CampoInput
              id="apelidoUsuario"
              label="Nickname"
              v-model="item.apelido"
              :readonly="isReadOnly"
              :class="{ 'is-invalid': apelidoDisponivel === false }"
            />
            <small v-if="apelidoDisponivel === false" class="text-danger">Este nickname já está em uso.</small>
            <small v-else-if="apelidoDisponivel === true" class="text-success">Nickname disponível!</small>
            <small v-else class="text-body-secondary">Apenas letras, números e underscore. Para @menções.</small>
          </div>

          <!-- Foto Interna -->
          <div class="col-md-3">
            <label class="form-label">Foto Interna</label>
            <div class="d-flex align-items-center gap-2">
              <div v-if="previewFotoInterna" class="foto-preview">
                <img :src="previewFotoInterna" alt="Foto Interna" class="rounded-circle" width="64" height="64" style="object-fit: cover;" />
              </div>
              <div v-else class="foto-preview-placeholder rounded-circle d-flex align-items-center justify-content-center bg-secondary text-white" style="width:64px;height:64px;">
                <i class="bi bi-person fs-3"></i>
              </div>
              <div>
                <input type="file" accept="image/*" class="form-control form-control-sm" @change="onFotoInternaChange" :disabled="isReadOnly" />
                <button v-if="item.urlFotoInterna" type="button" class="btn btn-sm btn-outline-danger mt-1" @click="removerFotoInterna" :disabled="isReadOnly">
                  <i class="bi bi-trash"></i> Remover
                </button>
              </div>
            </div>
          </div>

          <!-- Foto Pública -->
          <div class="col-md-3">
            <label class="form-label">Foto Pública</label>
            <div class="d-flex align-items-center gap-2">
              <div v-if="previewFotoPublica" class="foto-preview">
                <img :src="previewFotoPublica" alt="Foto Pública" class="rounded-circle" width="64" height="64" style="object-fit: cover;" />
              </div>
              <div v-else class="foto-preview-placeholder rounded-circle d-flex align-items-center justify-content-center bg-secondary text-white" style="width:64px;height:64px;">
                <i class="bi bi-globe fs-3"></i>
              </div>
              <div>
                <input type="file" accept="image/*" class="form-control form-control-sm" @change="onFotoPublicaChange" :disabled="isReadOnly" />
                <button v-if="item.urlFotoPublica" type="button" class="btn btn-sm btn-outline-danger mt-1" @click="removerFotoPublica" :disabled="isReadOnly">
                  <i class="bi bi-trash"></i> Remover
                </button>
              </div>
            </div>
          </div>

          <!-- Aceite Termo -->
          <div class="col-12">
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                id="aceiteTermo"
                v-model="item.aceiteTermoUso"
                :disabled="isReadOnly"
              />
              <label class="form-check-label" for="aceiteTermo">
                Aceito o <a href="#" @click.prevent="abrirTermoUso">Termo de Uso para exibição pública</a>
              </label>
            </div>
          </div>

          <!-- Aceite Notificações -->
          <div class="col-12">
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                id="aceiteNotificacoes"
                v-model="item.aceiteNotificacoes"
                :disabled="isReadOnly"
              />
              <label class="form-check-label" for="aceiteNotificacoes">
                Aceito receber notificações de interações nas minhas sugestões
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>

    <!-- Frame 6: Histórico de Logins -->
    <div :class="{ 'pe-none opacity-50': isFramesSecundariosBloqueados }">
    <div class="card mb-4" v-if="isEditing">
      <div
        class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.historicoLogin = !accordionState.historicoLogin"
      >
        <h5 class="mb-0"><i class="bi bi-key me-2 text-primary"></i> Histórico de Logins</h5>
        <i :class="['bi', accordionState.historicoLogin ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.historicoLogin">
        <!-- Filtro de período -->
        <div class="d-flex align-items-end gap-2 mb-3">
          <div style="min-width: 300px; flex-shrink: 0;">
            <CampoDataIntervalo
              id="filtroPeriodoLogin"
              label="Período"
              v-model:model-value-inicio="filtroLogin.dataInicio"
              v-model:model-value-fim="filtroLogin.dataFim"
              hide-label
            />
          </div>
          <div class="pb-1">
            <button type="button" class="btn btn-sm btn-outline-primary" @click="buscarHistoricoLogin(1)">
              <i class="bi bi-search me-1"></i> Filtrar
            </button>
          </div>
        </div>

        <!-- Tabela de resultados -->
        <div v-if="isLoadingHistoricoLogin" class="text-center py-3">
          <span class="spinner-border spinner-border-sm me-2"></span>Carregando...
        </div>
        <div v-else-if="historicoLogin.length === 0" class="text-muted small text-center py-3">
          Nenhum registro de login encontrado.
        </div>
        <div v-else class="table-responsive">
          <table class="table table-sm table-hover align-middle">
            <thead class="table-light">
              <tr>
                <th>Data/Hora</th>
                <th>Email</th>
                <th>IP</th>
                <th>Status</th>
                <th>Motivo</th>
                <th>Logout</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="log in historicoLogin" :key="log.idHistoricoLogin">
                <td>{{ formatarDataHoraBR(log.dataHoraLogin) }}</td>
                <td><code class="small">{{ log.email }}</code></td>
                <td><code class="small">{{ log.ip || '-' }}</code></td>
                <td>
                  <span :class="['badge', log.sucesso ? 'badge-ghost-success' : 'badge-ghost-danger']">
                    {{ log.sucesso ? 'Sucesso' : 'Falha' }}
                  </span>
                </td>
                <td class="small">{{ log.motivoFalha || '-' }}</td>
                <td class="small">{{ log.dataHoraLogout ? formatarDataHoraBR(log.dataHoraLogout) : '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginação -->
        <div v-if="paginacaoLogin.totalPages > 0" class="d-flex justify-content-between align-items-center mt-2">
          <small class="text-muted">Total: {{ paginacaoLogin.totalCount }} registro(s)</small>
          <nav>
            <ul class="pagination pagination-sm mb-0">
              <li class="page-item" :class="{ disabled: paginacaoLogin.currentPage <= 1 }">
                <button class="page-link" @click="mudarPaginaLogin(paginacaoLogin.currentPage - 1)">«</button>
              </li>
              <li class="page-item" v-for="p in paginacaoLogin.totalPages" :key="p"
                  :class="{ active: p === paginacaoLogin.currentPage }">
                <button class="page-link" @click="mudarPaginaLogin(p)">{{ p }}</button>
              </li>
              <li class="page-item" :class="{ disabled: paginacaoLogin.currentPage >= paginacaoLogin.totalPages }">
                <button class="page-link" @click="mudarPaginaLogin(paginacaoLogin.currentPage + 1)">»</button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
    </div>
  </FormularioCadastro>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="Usuario"
    :chave-primaria="item.idUsuario"
    :titulo-registro="item.nome"
    :dicionarios="{'statusUsuario': opcoesStatus}"
    @fechar="mostrarAuditoria = false"
  />

  <!-- Modal de Busca de Grupos de Acesso -->
  <ModalDeBusca
    v-if="mostrarModalGrupos"
    :configuracao="configBuscaGrupos"
    :selecao-multipla="true"
    :itens-ja-selecionados="gruposSelecionados"
    :on-show-notification="(payload) => emit('show-notification', payload)"
    @itens-selecionados="onGruposSelecionados"
    @fechar="mostrarModalGrupos = false"
  />

  <!-- Modal de Busca de Organizações -->
  <ModalDeBusca
    v-if="mostrarModalOrganizacoes"
    :configuracao="configBuscaOrganizacoes"
    :selecao-multipla="true"
    :itens-ja-selecionados="organizacoesSelecionadas"
    :on-show-notification="(payload) => emit('show-notification', payload)"
    @itens-selecionados="onOrganizacoesSelecionadas"
    @fechar="mostrarModalOrganizacoes = false"
  />
</template>

<script setup>
import { ref, reactive, watch, onMounted, computed } from 'vue'
import api from '@/services/api' // GUARDIÃO: Uso da API Global obrigatório
import { useFormulario } from '@/composables/useFormulario.js'
import { useAuthStore } from '@/stores/auth.js'
import { formatarDataHoraBR } from '@/utils/useDataBR'
import CampoDataIntervalo from '@/components/base/campos/CampoDataIntervalo.vue'
import FormularioCadastro from '../base/formulario/FormularioCadastro.vue'
import CampoInput from '../base/campos/CampoInput.vue'
import CampoBusca from '../base/campos/CampoBusca.vue'
import CampoSelect from '../base/campos/CampoSelect.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import ModalDeBusca from '@/components/base/modal/ModalDeBusca.vue'
import { configBuscaEmpresa } from '@/search-definitions/empresaSearch.js'
import { criarConfigBuscaGrupoAcesso } from '@/search-definitions/grupoAcessoSearch.js'
import { configBuscaOrganizacao } from '@/search-definitions/organizacaoSearch.js'
import PermissoesAcessoSelector from '@/components/base/permissoes/PermissoesAcessoSelector.vue'

const authStore = useAuthStore()

// Estado do Accordion (padrão seção 14 das diretrizes)
const accordionState = reactive({
  dadosBasicos: true,
  gruposAcesso: false,
  organizacoes: false,
  regrasAcesso: false,
  configToken: false,
  perfilPublico: false,
  historicoLogin: false
})

// Estado Configuração Token JWT
const tokenExpiracaoConfig = ref(null)
const isLoadingTokenConfig = ref(false)

const carregarTokenExpiracaoConfig = async () => {
  if (!item.value.idUsuario) return
  isLoadingTokenConfig.value = true
  try {
    const response = await api.get(`/Usuarios/${item.value.idUsuario}/token-expiracao-config`)
    tokenExpiracaoConfig.value = response.data
    // Mapeia os valores retornados para o item (v-model do formulário)
    if (response.data.origemAtiva) {
      item.value.tokenExpiracaoOrigemAtiva = response.data.origemAtiva
    }
    if (response.data.personalizadoAccessToken != null) {
      item.value.tokenExpiracaoPersonalizadoMinutos = response.data.personalizadoAccessToken
    }
  } catch (error) {
    console.error('Erro ao carregar config de token:', error)
  } finally {
    isLoadingTokenConfig.value = false
  }
}

const props = defineProps({
  itemParaEditar: { type: Object, default: null },
  modeloVazio: {
    type: Object,
    default: () => ({
      idUsuario: 0,
      nome: '',
      email: '',
      idEmpresa: null,
      adminInterno: false,
      adminCliente: false,
      statusUsuario: 'Ativo',
      nomeExibicao: '',
      apelido: '',
      urlFotoInterna: null,
      urlFotoPublica: null,
      aceiteTermoUso: false,
      aceiteNotificacoes: false,
      tokenExpiracaoPersonalizadoMinutos: null,
      tokenExpiracaoOrigemAtiva: 'FALLBACK'
    })
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'changesDetected', 'show-notification'])

const {
  item, isLoading, isEditing, entityInfo, submitForm, handleCancel, initializeForm
} = useFormulario(props, emit)

// ============================================================
// ESTADO DE SALVAMENTO (Salvar e Continuar)
// ============================================================
const isUsuarioSaved = ref(isEditing.value)
const isSavingUsuarioLocal = ref(false)
const isFramesSecundariosBloqueados = computed(() => !isUsuarioSaved.value || isLoading.value)

// ============================================================
// FUNÇÃO: Salvar Usuário Localmente (criação inicial)
// ============================================================
const salvarUsuarioLocalmente = async () => {
  if (isSavingUsuarioLocal.value) return

  if (!item.value.nome) {
    emit('show-notification', { message: 'O campo Nome é obrigatório.', type: 'warning' })
    return
  }
  if (!item.value.email) {
    emit('show-notification', { message: 'O campo Email é obrigatório.', type: 'warning' })
    return
  }

  isSavingUsuarioLocal.value = true
  const payload = {
    Nome: item.value.nome,
    Email: item.value.email,
    IdEmpresa: item.value.idEmpresa,
    AdminInterno: item.value.adminInterno,
    AdminCliente: item.value.adminCliente,
    StatusUsuario: item.value.statusUsuario,
    NomeExibicao: item.value.nomeExibicao || null,
    Apelido: item.value.apelido || null,
    AceiteTermoUso: item.value.aceiteTermoUso,
    AceiteNotificacoes: item.value.aceiteNotificacoes,
    GruposIds: [],
    PermissoesDiretasIds: [],
    OrganizacoesIds: [],
    IdOrganizacaoPadrao: null
  }

  try {
    const response = await api.post('/Usuarios', payload)
    item.value = response.data
    isUsuarioSaved.value = true
    isEditing.value = true
    entityInfo.value = { id: item.value.idUsuario, name: item.value.nome, nameLabel: 'Usuário' }
    emit('show-notification', { message: 'Usuário salvo. Agora pode configurar grupos e permissões.', type: 'success' })
    accordionState.gruposAcesso = true
    accordionState.regrasAcesso = true
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar o usuário.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isSavingUsuarioLocal.value = false
  }
}

// ============================================================
// FUNÇÃO: Salvar Alterações (via @submit-continuar)
// ============================================================
const salvarAlteracoes = async () => {
  if (isLoading.value || !isUsuarioSaved.value) return

  const payload = {
    Nome: item.value.nome,
    Email: item.value.email,
    IdEmpresa: item.value.idEmpresa,
    AdminInterno: item.value.adminInterno,
    AdminCliente: item.value.adminCliente,
    StatusUsuario: item.value.statusUsuario,
    NomeExibicao: item.value.nomeExibicao || null,
    Apelido: item.value.apelido || null,
    UrlFotoInterna: item.value.urlFotoInterna,
    UrlFotoPublica: item.value.urlFotoPublica,
    AceiteTermoUso: item.value.aceiteTermoUso,
    AceiteNotificacoes: item.value.aceiteNotificacoes,
    GruposIds: gruposSelecionadosIds.value.slice(),
    PermissoesDiretasIds: permissoesDiretasIds.value.slice(),
    OrganizacoesIds: organizacoesSelecionadas.value.map(o => o.idOrganizacao),
    IdOrganizacaoPadrao: idOrganizacaoPadrao.value,
    TokenExpiracaoPersonalizadoMinutos: item.value.tokenExpiracaoPersonalizadoMinutos,
    TokenExpiracaoOrigemAtiva: item.value.tokenExpiracaoOrigemAtiva
  }
  if (isEditing.value && item.value.idUsuario) payload.IdUsuario = item.value.idUsuario

  try {
    const res = await api.put(`/Usuarios/${item.value.idUsuario}`, payload)
    if (res?.data) {
      initializeForm(res.data)
    }
    emit('show-notification', { message: 'Alterações salvas com sucesso!', type: 'success' })
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar alterações.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  }
}

const temPermissaoCompleta = computed(() => authStore.pode('USUARIOS_COMPLETO'))
const isReadOnly = computed(() => {
  return isEditing.value && !temPermissaoCompleta.value
})

const mostrarAuditoria = ref(false)
const isLoadingReset = ref(false)

// GUARDIÃO: Array de opções universais Sim/Não
const simNaoOptions = [
  { id: true, label: 'Sim' },
  { id: false, label: 'Não' }
]

const opcoesStatus = [
  { id: 'Ativo', descricao: 'Ativo' },
  { id: 'Inativo', descricao: 'Inativo' }
]

// ==================== PERFIL PÚBLICO ====================
const apelidoDisponivel = ref(null)
let debounceApelido = null

const previewFotoInterna = ref(null)
const previewFotoPublica = ref(null)

const verificarApelido = async () => {
  const apelido = item.value.apelido
  if (!apelido || apelido.length < 3) {
    apelidoDisponivel.value = null
    return
  }
  try {
    const response = await api.get('/Usuarios/apelido-disponivel', { params: { apelido } })
    apelidoDisponivel.value = response.data.disponivel
  } catch {
    apelidoDisponivel.value = null
  }
}

watch(() => item.value.apelido, () => {
  if (debounceApelido) clearTimeout(debounceApelido)
  debounceApelido = setTimeout(verificarApelido, 300)
})

const onFotoInternaChange = async (event) => {
  const file = event.target.files[0]
  if (!file) return
  previewFotoInterna.value = URL.createObjectURL(file)
  if (item.value.idUsuario) {
    const formData = new FormData()
    formData.append('file', file)
    try {
      const response = await api.post(`/Usuarios/${item.value.idUsuario}/foto-interna`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      item.value.urlFotoInterna = response.data.url
    } catch (error) {
      emit('show-notification', { message: 'Erro ao fazer upload da foto interna.', type: 'error' })
    }
  }
}

const onFotoPublicaChange = async (event) => {
  const file = event.target.files[0]
  if (!file) return
  previewFotoPublica.value = URL.createObjectURL(file)
  if (item.value.idUsuario) {
    const formData = new FormData()
    formData.append('file', file)
    try {
      const response = await api.post(`/Usuarios/${item.value.idUsuario}/foto-publica`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      item.value.urlFotoPublica = response.data.url
    } catch (error) {
      emit('show-notification', { message: 'Erro ao fazer upload da foto pública.', type: 'error' })
    }
  }
}

const removerFotoInterna = () => {
  previewFotoInterna.value = null
  item.value.urlFotoInterna = null
}

const removerFotoPublica = () => {
  previewFotoPublica.value = null
  item.value.urlFotoPublica = null
}

const abrirTermoUso = () => {
  window.open('/Docs%20CT%20Comex/termo_uso_perfil_publico.md', '_blank')
}

// ==================== GRUPOS ====================
// Estado Grupos
const todosOsGrupos = ref([])
const gruposSelecionadosIds = ref([])
const gruposSelecionados = ref([])
const isLoadingGrupos = ref(false)
const erroGrupos = ref(null)
const mostrarModalGrupos = ref(false)
const configBuscaGrupos = ref(null)

// ==================== ORGANIZAÇÕES (multi-organização) ====================
const organizacoesSelecionadas = ref([])
const idOrganizacaoPadrao = ref(null)
const mostrarModalOrganizacoes = ref(false)
const configBuscaOrganizacoes = ref(null)

const abrirModalOrganizacoes = () => {
  configBuscaOrganizacoes.value = configBuscaOrganizacao
  mostrarModalOrganizacoes.value = true
}

const onOrganizacoesSelecionadas = (itens) => {
  if (!Array.isArray(itens)) return
  organizacoesSelecionadas.value = itens
  // Se a organização padrão atual não está mais na lista, redefine para a primeira
  if (idOrganizacaoPadrao.value && !itens.some(o => o.idOrganizacao === idOrganizacaoPadrao.value)) {
    idOrganizacaoPadrao.value = itens.length > 0 ? itens[0].idOrganizacao : null
  }
  if (!idOrganizacaoPadrao.value && itens.length > 0) {
    idOrganizacaoPadrao.value = itens[0].idOrganizacao
  }
  mostrarModalOrganizacoes.value = false
}

const removerOrganizacaoSelecionada = (idOrganizacao) => {
  organizacoesSelecionadas.value = organizacoesSelecionadas.value.filter(o => o.idOrganizacao !== idOrganizacao)
  if (idOrganizacaoPadrao.value === idOrganizacao) {
    idOrganizacaoPadrao.value = organizacoesSelecionadas.value.length > 0 ? organizacoesSelecionadas.value[0].idOrganizacao : null
  }
}

const buscarOrganizacoesDoUsuario = async (idUsuario) => {
  if (!idUsuario) return
  try {
    const response = await api.get(`/Usuarios/${idUsuario}/organizacoes`)
    const data = Array.isArray(response.data) ? response.data : []
    organizacoesSelecionadas.value = data
    const padrao = data.find(o => o.isPadrao)
    idOrganizacaoPadrao.value = padrao ? padrao.idOrganizacao : (data.length > 0 ? data[0].idOrganizacao : null)
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar organizações do usuário.', type: 'error' })
    organizacoesSelecionadas.value = []
    idOrganizacaoPadrao.value = null
  }
}

// Estado Permissões
const permissoesConsolidadas = ref([])
const permissoesDiretasIds = ref([])
const isLoadingPermissoes = ref(false)
const erroPermissoes = ref(null)

// Estado Histórico de Logins
const historicoLogin = ref([])
const isLoadingHistoricoLogin = ref(false)
const filtroLogin = reactive({
  dataInicio: '',
  dataFim: ''
})
const paginacaoLogin = reactive({
  currentPage: 1,
  totalPages: 0,
  totalCount: 0,
  pageSize: 10
})

const buscarHistoricoLogin = async (page = 1) => {
  if (!item.value.idUsuario) return
  isLoadingHistoricoLogin.value = true
  try {
    const params = { page, pageSize: paginacaoLogin.pageSize, idUsuario: item.value.idUsuario }
    if (filtroLogin.dataInicio) params.dataInicio = filtroLogin.dataInicio
    if (filtroLogin.dataFim) params.dataFim = filtroLogin.dataFim
    // AdminInterno (sem empresa) → Master; usuário de cliente → Tenant
    const endpoint = item.value.adminInterno ? '/HistoricoLoginMaster' : '/HistoricoLoginTenant'
    const response = await api.get(endpoint, { params })
    historicoLogin.value = response.data
    const pagination = JSON.parse(response.headers['x-pagination'] || '{}')
    paginacaoLogin.currentPage = pagination.currentPage || page
    paginacaoLogin.totalPages = pagination.totalPages || 0
    paginacaoLogin.totalCount = pagination.totalCount || 0
  } catch (error) {
    console.error('Erro ao carregar histórico de logins:', error)
    historicoLogin.value = []
  } finally {
    isLoadingHistoricoLogin.value = false
  }
}

const mudarPaginaLogin = (page) => {
  if (page < 1 || page > paginacaoLogin.totalPages) return
  buscarHistoricoLogin(page)
}

// Carregar histórico ao abrir o accordion
watch(() => accordionState.historicoLogin, (val) => {
  if (val && historicoLogin.value.length === 0) {
    buscarHistoricoLogin(1)
  }
})

// Computed Permissões
const buscarPermissoesConsolidadas = async (idEmpresaOverride) => {
  isLoadingPermissoes.value = true; erroPermissoes.value = null
  try {
    if (idEmpresaOverride) {
      const response = await api.get(`/EmpresasPermissoesAcesso/empresa/${idEmpresaOverride}/catalogo-contratado`)
      permissoesConsolidadas.value = Array.isArray(response.data) ? response.data : (response.data?.items || [])
    } else {
      const response = await api.get('/RegraAcessoPermissao/catalogo')
      permissoesConsolidadas.value = Array.isArray(response.data) ? response.data : (response.data?.items || [])
    }
  } catch (error) {
    erroPermissoes.value = 'Não foi possível carregar as permissões de acesso.'
    permissoesConsolidadas.value = []
  } finally { isLoadingPermissoes.value = false }
}

const permissoesHerdadas = ref([])

const buscarPermissoesDiretasDoUsuario = async (idUsuario) => {
  if (!idUsuario) return
  try {
    const response = await api.get(`/Usuarios/${idUsuario}/permissoes-efetivas`)
    const data = response.data
    if (Array.isArray(data)) {
      // Formato único: [{ idRegraAcessoPermissao, herdada, nomeGrupo }]
      permissoesDiretasIds.value = data
        .filter(item => !item.herdada)
        .map(item => item.idRegraAcessoPermissao)
      permissoesHerdadas.value = data
        .filter(item => item.herdada)
        .map(item => ({ idRegraAcessoPermissao: item.idRegraAcessoPermissao, nomeGrupo: item.nomeGrupo }))
    } else {
      permissoesDiretasIds.value = []
      permissoesHerdadas.value = []
    }
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar permissões individuais.', type: 'error' })
    permissoesDiretasIds.value = []
    permissoesHerdadas.value = []
  }
}

const buscarTodosOsGrupos = async (idEmpresaAlvo) => {
  if (!idEmpresaAlvo) { todosOsGrupos.value = []; return }
  isLoadingGrupos.value = true; erroGrupos.value = null
  try {
    let url = '/GruposAcesso?pageSize=1000'
    if (authStore.isAdminInterno) url += `&targetIdEmpresa=${idEmpresaAlvo}`
    const response = await api.get(url)
    todosOsGrupos.value = Array.isArray(response.data) ? response.data : (response.data?.items || [])
  } catch (error) {
    erroGrupos.value = 'Não foi possível carregar os grupos de acesso.'
    todosOsGrupos.value = []
  } finally { isLoadingGrupos.value = false }
}
const buscarGruposDoUsuario = async (idUsuario) => {
  if (!idUsuario) return
  try {
    const response = await api.get(`/Usuarios/${idUsuario}/grupos`)
    const ids = Array.isArray(response.data) ? response.data : []
    gruposSelecionadosIds.value = ids
    // Resolve objetos completos a partir de todosOsGrupos
    gruposSelecionados.value = todosOsGrupos.value.filter(g => ids.includes(g.idGrupoAcesso))
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar grupos associados.', type: 'error' })
    gruposSelecionadosIds.value = []
    gruposSelecionados.value = []
  }
}

// ── Modal de Busca de Grupos ──
const abrirModalGrupos = () => {
  const idOrganizacao = authStore.organizacaoAtiva || item.value.idEmpresa
  if (!idOrganizacao) {
    emit('show-notification', { message: 'Selecione uma organização primeiro.', type: 'warning' })
    return
  }
  configBuscaGrupos.value = criarConfigBuscaGrupoAcesso(idOrganizacao)
  mostrarModalGrupos.value = true
}

const onGruposSelecionados = (itens) => {
  if (!Array.isArray(itens)) return
  // Substitui a lista atual pelos itens selecionados no modal
  gruposSelecionados.value = itens
  gruposSelecionadosIds.value = itens.map(g => g.idGrupoAcesso)
  mostrarModalGrupos.value = false
}

const removerGrupoSelecionado = (idGrupo) => {
  gruposSelecionados.value = gruposSelecionados.value.filter(g => g.idGrupoAcesso !== idGrupo)
  gruposSelecionadosIds.value = gruposSelecionadosIds.value.filter(id => id !== idGrupo)
}

onMounted(async () => {
  let idEmpresaInicial = null
  if (!authStore.isAdminInterno) {
    item.value.idEmpresa = authStore.idEmpresaUtilizador
    idEmpresaInicial = authStore.idEmpresaUtilizador
  } else {
    idEmpresaInicial = props.itemParaEditar?.idEmpresa || authStore.empresaAtiva
    if (!isEditing.value) item.value.idEmpresa = idEmpresaInicial
  }

  // FASE 1: Carrega dados independentes em paralelo (permissoes, grupos disponíveis)
  const promises = [buscarPermissoesConsolidadas(idEmpresaInicial)]
  if (idEmpresaInicial) promises.push(buscarTodosOsGrupos(idEmpresaInicial))
  await Promise.allSettled(promises)

  // FASE 2: Agora que todosOsGrupos está populado, carrega dados do usuário
  // (buscarGruposDoUsuario depende de todosOsGrupos para resolver objetos completos)
  if (isEditing.value && item.value.idUsuario) {
    await Promise.allSettled([
      buscarGruposDoUsuario(item.value.idUsuario),
      buscarPermissoesDiretasDoUsuario(item.value.idUsuario),
      buscarOrganizacoesDoUsuario(item.value.idUsuario),
      carregarTokenExpiracaoConfig()
    ])
  }
})

watch(() => item.value.adminInterno, (isInterno) => {
  gruposSelecionadosIds.value = []
  gruposSelecionados.value = []
  permissoesDiretasIds.value = []
  permissoesHerdadas.value = []
  organizacoesSelecionadas.value = []
  idOrganizacaoPadrao.value = null
  if (isInterno) {
    item.value.idEmpresa = null
    item.value.adminCliente = false
    todosOsGrupos.value = []
    permissoesConsolidadas.value = []
  } else {
    const idEmpresaParaBuscar = authStore.empresaAtiva
    item.value.idEmpresa = idEmpresaParaBuscar
    if (idEmpresaParaBuscar) {
      buscarTodosOsGrupos(idEmpresaParaBuscar)
      buscarPermissoesConsolidadas(idEmpresaParaBuscar)
    } else {
      todosOsGrupos.value = []
      permissoesConsolidadas.value = []
    }
  }
})

const onEmpresaChange = (novoIdEmpresa) => {
  gruposSelecionadosIds.value = []
  gruposSelecionados.value = []
  permissoesDiretasIds.value = []
  permissoesHerdadas.value = []
  if (authStore.isAdminInterno) {
    if (novoIdEmpresa) {
      buscarTodosOsGrupos(novoIdEmpresa)
      // Recarrega as permissões de acesso disponíveis filtradas pela nova empresa
      buscarPermissoesConsolidadas(novoIdEmpresa)
    } else {
      todosOsGrupos.value = []
      permissoesConsolidadas.value = []
    }
  }
}

watch([gruposSelecionadosIds, permissoesDiretasIds], () => {
  if (!isLoading.value && !isLoadingGrupos.value && !isLoadingPermissoes.value) {
    emit('changes-detected', true)
  }
}, { deep: true })

const submitFormComGruposERegras = async () => {
  if (isReadOnly.value) return
  const payload = {
    Nome: item.value.nome,
    Email: item.value.email,
    IdEmpresa: item.value.idEmpresa,
    AdminInterno: item.value.adminInterno,
    AdminCliente: item.value.adminCliente,
    StatusUsuario: item.value.statusUsuario,
    NomeExibicao: item.value.nomeExibicao || null,
    Apelido: item.value.apelido || null,
    UrlFotoInterna: item.value.urlFotoInterna,
    UrlFotoPublica: item.value.urlFotoPublica,
    AceiteTermoUso: item.value.aceiteTermoUso,
    AceiteNotificacoes: item.value.aceiteNotificacoes,
    GruposIds: gruposSelecionadosIds.value.slice(),
    PermissoesDiretasIds: permissoesDiretasIds.value.slice(),
    OrganizacoesIds: organizacoesSelecionadas.value.map(o => o.idOrganizacao),
    IdOrganizacaoPadrao: idOrganizacaoPadrao.value,
    TokenExpiracaoPersonalizadoMinutos: item.value.tokenExpiracaoPersonalizadoMinutos,
    TokenExpiracaoOrigemAtiva: item.value.tokenExpiracaoOrigemAtiva
  }
  if (isEditing.value && item.value.idUsuario) payload.IdUsuario = item.value.idUsuario
  await submitForm('Usuarios', 'idUsuario', payload)
}

const resetarSenha = async () => {
  if (!item.value.idUsuario || isReadOnly.value) return
  isLoadingReset.value = true
  try {
    const response = await api.post(`/Usuarios/${item.value.idUsuario}/reset-senha`)
    emit('show-notification', { message: response.data.message, type: 'success' })
  } catch (error) {
    emit('show-notification', { message: 'Não foi possível enviar o e-mail de reset.', type: 'error' })
  } finally { isLoadingReset.value = false }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

/* Acordeões e Listas */
.accordion-body .list-group-item {
  border: none;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  background-color: transparent;
}
</style>
